import React from 'react'
import { useTranslation } from 'react-i18next'
import { Typogaphy } from 'vnetwork-ui'
import { SEARCH_RESULT_TYPES } from '../../constants'
import { SearchResultType } from '../../types'
import PeopleIcons from '@/icons/PeopleIcons'
import ServiceIcons from '@/icons/ServiceIcons'
import InvoiceIcons from '@/icons/InvoiceIcons'
import { Checkbox } from '@/components/ui/checkbox'

interface ScopeSelectorProps {
    selectedScopes: SearchResultType[]
    onScopeChange: (scopes: SearchResultType[]) => void
    className?: string
}

const SCOPE_ICONS: Record<SearchResultType, React.ReactNode> = {
    customer: <PeopleIcons className="w-4 h-4" />,
    service: <ServiceIcons className="w-4 h-4" />,
    contract: <InvoiceIcons className="w-4 h-4" />,
    invoice: <InvoiceIcons className="w-4 h-4" />,
}

const ScopeSelector: React.FC<ScopeSelectorProps> = ({
    selectedScopes,
    onScopeChange,
    className = '',
}) => {
    const { t } = useTranslation('search')

    const handleScopeToggle = (scope: SearchResultType) => {
        const isSelected = selectedScopes.includes(scope)
        if (isSelected) {
            onScopeChange(selectedScopes.filter((s) => s !== scope))
        } else {
            onScopeChange([...selectedScopes, scope])
        }
    }

    const handleSelectAll = () => {
        const allScopes = Object.keys(SEARCH_RESULT_TYPES) as SearchResultType[]
        if (selectedScopes.length === allScopes.length) {
            onScopeChange([])
        } else {
            onScopeChange(allScopes)
        }
    }

    const allScopes = Object.keys(SEARCH_RESULT_TYPES) as SearchResultType[]
    const isAllSelected = selectedScopes.length === allScopes.length

    return (
        <div className={`space-y-3 ${className}`}>
            <div className="flex items-center justify-between">
                <Typogaphy className="text-sm font-medium text-gray-700">
                    {t('search-scope')}
                </Typogaphy>
                <button
                    onClick={handleSelectAll}
                    className="text-xs text-blue-600 hover:text-blue-800"
                >
                    {isAllSelected ? t('deselect-all') : t('select-all')}
                </button>
            </div>

            <div className="grid grid-cols-2 gap-2">
                {allScopes.map((scope) => {
                    const config = SEARCH_RESULT_TYPES[scope]
                    if (!config) return null
                    const isSelected = selectedScopes.includes(scope)

                    return (
                        <div
                            key={scope}
                            onClick={() => handleScopeToggle(scope)}
                            className={`
                                flex items-center gap-2 p-2 rounded-sm border cursor-pointer transition-all
                                ${
                                    isSelected
                                        ? 'border-blue-500 bg-blue-50'
                                        : 'border-gray-200 hover:border-gray-300 hover:bg-gray-50'
                                }
                            `}
                        >
                            <Checkbox
                                checked={isSelected}
                                onChange={() => handleScopeToggle(scope)}
                            />
                            <div className="flex items-center gap-1.5">
                                {SCOPE_ICONS[scope]}
                                <Typogaphy className="text-sm">
                                    {t(config.label)}
                                </Typogaphy>
                            </div>
                        </div>
                    )
                })}
            </div>
        </div>
    )
}

export default ScopeSelector
